import { errorLogger } from '../utils/errorLogger';
import { getCircuitBreakerState } from '../utils/retryMechanism';

/**
 * Error Monitoring Service
 * Provides centralized error handling and monitoring for Supabase operations
 */

const errorMonitoring = {
  /**
   * Handle query errors with retry awareness
   */
  handleQueryError(error, queryDetails = {}, userContext = {}, showToast = true) {
    // Check circuit breaker state
    const circuitState = getCircuitBreakerState();
    
    // Log the error
    errorLogger?.logQueryError(error, queryDetails, userContext);

    // Show user-friendly error message
    if (showToast && typeof window !== 'undefined') {
      let errorMessage = 'An error occurred while processing your request.';
      
      // Circuit breaker open
      if (error?.circuitOpen || circuitState.state === 'open') {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      }
      // Retry exhausted
      else if (error?.message?.includes('Max retries')) {
        errorMessage = 'Unable to complete request after multiple attempts. Please try again later.';
      }
      // Specific error messages
      else if (error?.message) {
        errorMessage = error.message;
      }

      console.error('[Query Error]', errorMessage, error);
    }

    return error;
  },

  /**
   * Handle authentication errors with retry awareness
   */
  handleAuthError(error, authContext = {}, showToast = true) {
    // Check circuit breaker state
    const circuitState = getCircuitBreakerState();
    
    // Log the error
    errorLogger?.logAuthError(error, authContext);

    // Show user-friendly error message
    if (showToast && typeof window !== 'undefined') {
      let errorMessage = 'Authentication failed. Please try again.';
      
      // Circuit breaker open
      if (error?.circuitOpen || circuitState.state === 'open') {
        errorMessage = 'Authentication service temporarily unavailable. Please try again in a moment.';
      }
      // Retry exhausted
      else if (error?.message?.includes('Max retries')) {
        errorMessage = 'Unable to authenticate after multiple attempts. Please try again later.';
      }
      // Specific error messages
      else if (error?.message) {
        errorMessage = error.message;
      }

      console.error('[Auth Error]', errorMessage, error);
    }

    return error;
  },

  /**
   * Handle RLS policy violation
   * @param {Error} error - The error object
   * @param {object} policyDetails - Policy details
   * @param {object} userContext - User context
   * @param {boolean} showUserMessage - Whether to show toast to user
   */
  handlePolicyViolation(error, policyDetails = {}, userContext = {}, showUserMessage = true) {
    // Log the error
    const errorLog = errorLogger?.logPolicyViolation(error, policyDetails, userContext);

    // Show user-friendly message
    if (showUserMessage) {
      showToast('You don\'t have permission to perform this action', 'error');
    }

    return errorLog;
  },

  /**
   * Handle network error
   * @param {Error} error - The error object
   * @param {object} context - Additional context
   * @param {boolean} showUserMessage - Whether to show toast to user
   */
  handleNetworkError(error, context = {}, showUserMessage = true) {
    // Log the error
    const errorLog = errorLogger?.logNetworkError(error, context);

    // Show user-friendly message
    if (showUserMessage) {
      showToast('Connection error. Please check your internet and try again.', 'error');
    }

    return errorLog;
  },

  /**
   * Wrap a Supabase query with error handling
   * @param {Function} queryFn - The query function to execute
   * @param {object} queryDetails - Query details for logging
   * @param {object} userContext - User context
   * @returns {Promise} Query result
   */
  async wrapQuery(queryFn, queryDetails = {}, userContext = {}) {
    try {
      const result = await queryFn();
      
      // Check for Supabase error in response
      if (result?.error) {
        this._handleSupabaseError(result?.error, queryDetails, userContext);
        return { data: null, error: result?.error };
      }
      
      return result;
    } catch (error) {
      // Handle network or unexpected errors
      if (this._isNetworkError(error)) {
        this.handleNetworkError(error, queryDetails);
      } else {
        this.handleQueryError(error, queryDetails, userContext);
      }
      
      return { data: null, error };
    }
  },

  /**
   * Check if error rate exceeds threshold
   */
  _checkErrorThreshold() {
    const recentErrors = errorLogger?.getErrorLogs({
      startDate: new Date(Date.now() - this.timeWindow)?.toISOString(),
    });

    if (recentErrors?.length >= this.errorThreshold) {
      console.warn(`⚠️ High error rate detected: ${recentErrors?.length} errors in the last 5 minutes`);
      // Could trigger additional alerting here (e.g., send to monitoring service)
    }
  },

  /**
   * Handle Supabase-specific errors
   */
  _handleSupabaseError(error, queryDetails, userContext) {
    // Check for specific error types
    if (this._isPolicyViolation(error)) {
      this.handlePolicyViolation(error, queryDetails, userContext);
    } else if (this._isNetworkError(error)) {
      this.handleNetworkError(error, queryDetails);
    } else {
      this.handleQueryError(error, queryDetails, userContext);
    }
  },

  /**
   * Check if error is a policy violation
   */
  _isPolicyViolation(error) {
    return (
      error?.code === '42501' || // PostgreSQL insufficient privilege
      error?.code === 'PGRST301' || // Supabase RLS policy violation
      error?.message?.toLowerCase()?.includes('permission') ||
      error?.message?.toLowerCase()?.includes('policy')
    );
  },

  /**
   * Check if error is a network error
   */
  _isNetworkError(error) {
    return (
      error?.message?.toLowerCase()?.includes('network') ||
      error?.message?.toLowerCase()?.includes('fetch') ||
      error?.message?.toLowerCase()?.includes('connection') ||
      error?.name === 'NetworkError' ||
      error?.name === 'TypeError' && error?.message?.includes('fetch')
    );
  },

  /**
   * Get user-friendly error message
   */
  _getUserFriendlyMessage(error, errorType) {
    // Check for specific error codes
    if (error?.code === 'PGRST116') {
      return 'No data found';
    }

    if (error?.code === '23505') {
      return 'This item already exists';
    }

    if (error?.code === '23503') {
      return 'Cannot complete action due to related data';
    }

    // Check for auth-specific errors
    if (errorType === ERROR_TYPES?.AUTH) {
      if (error?.message?.includes('Invalid login credentials')) {
        return 'Invalid email or password';
      }
      if (error?.message?.includes('Email not confirmed')) {
        return 'Please verify your email address';
      }
      if (error?.message?.includes('User already registered')) {
        return 'An account with this email already exists';
      }
    }

    // Check for network errors
    if (this._isNetworkError(error)) {
      return 'Connection error. Please check your internet and try again.';
    }

    // Check for policy violations
    if (this._isPolicyViolation(error)) {
      return 'You don\'t have permission to perform this action';
    }

    // Default messages by error type
    const defaultMessages = {
      [ERROR_TYPES?.QUERY]: 'Unable to load data. Please try again.',
      [ERROR_TYPES?.AUTH]: 'Authentication failed. Please try again.',
      [ERROR_TYPES?.POLICY_VIOLATION]: 'Access denied',
      [ERROR_TYPES?.NETWORK]: 'Connection error',
      [ERROR_TYPES?.MIGRATION]: 'System error',
    };

    return defaultMessages?.[errorType] || 'An unexpected error occurred';
  },

  /**
   * Get error statistics for admin dashboard
   */
  getErrorStatistics() {
    return errorLogger?.getStatistics();
  },

  /**
   * Get recent error logs
   */
  getRecentErrors(limit = 50) {
    const logs = errorLogger?.getErrorLogs();
    return logs?.slice(0, limit);
  },

  /**
   * Clear old error logs
   */
  clearOldLogs(days = 7) {
    errorLogger?.clearOldLogs(days);
  },

  /**
   * Export error logs
   */
  exportLogs() {
    return errorLogger?.exportLogs();
  }
};

// Export singleton instance
export { errorMonitoring };

export default errorMonitoring;
